import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import { FlatTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTreeModule,
} from '@angular/material/tree';
import { Router } from '@angular/router';
import { ArchObjHierarchyViewModel, ArchObjTreeViewModel } from '@core/api';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { LocationFormlyFieldService } from '@core/services/formly-service/location-formly-field.service';
import { SpeedikonService } from '@core/services/speedikon/speedikon.service';
import { FlatNode } from '@core/services/speedikon/types';
import { DocumentsModule } from '@features/documents/documents.module';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DocumentType, DocumentVisibility } from '@shared/types/document.types';
import { OverrideModule } from '../../../../app/override/override.module';
import { SharedModule } from '../../../../app/shared/shared.module';
@Component({
  selector: 'app-speedikon',
  templateUrl: './speedikon.component.html',
  styleUrls: ['./speedikon.component.scss'],
  imports: [
    CommonModule,
    SmartusComponentsModule,
    TranslateModule,
    OverrideModule,
    FormlyModule,
    MatTreeModule,
    MatIconModule,
    MatButtonModule,
    SharedModule,
    DocumentsModule,
    MatSlideToggleModule,
  ],
})
export class SpeedikonComponent implements OnInit, OnDestroy {
  readonly breadCrumbService = inject(BreadcrumbService);
  readonly translateService = inject(TranslateService);
  readonly locationFormlyFieldService = inject(LocationFormlyFieldService);
  readonly router = inject(Router);
  readonly speedikonService = inject(SpeedikonService);

  buildingDocument = this.speedikonService.state.documents;
  treeDataSignal = this.speedikonService.state.treeDataSignal;
  isLoading = this.speedikonService.state.isLoading;
  selectedLocation = signal<ArchObjHierarchyViewModel | null>(null);
  selectedItems = signal<string[]>([]);
  showDiv = signal(false);

  form = new FormGroup({});
  formFields: Array<FormlyFieldConfig>;
  formModel = {};

  defaultDocumentTypeValue = DocumentType.Bestandsplan;
  defaultDocumentVisibilityValue = DocumentVisibility.InternAndContractors;

  // eslint-disable-next-line class-methods-use-this
  readonly transformer = (node: ArchObjTreeViewModel, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.displayName,
      level: level,
      id: node.id,
    };
  };

  treeControl = new FlatTreeControl<FlatNode>(
    node => node.level,
    node => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this.transformer,
    node => node.level,
    node => node.expandable,
    (node: ArchObjTreeViewModel) => {
      return node.children || [];
    }
  );

  readonly treeDataSource = computed(() => {
    const treeData = this.treeDataSignal().getArchObjTree;

    const filterChildren = node => {
      if (!node.children) return [];

      return node.children
        .map((child: ArchObjTreeViewModel) => {
          if (child.type === 'ZGE') {
            return child;
          }

          return null;
        })
        .filter(Boolean);
    };

    const filteredTree = treeData
      ? {
          ...treeData,
          children: filterChildren(treeData),
        }
      : null;

    const dataSource = new MatTreeFlatDataSource(
      this.treeControl,
      this.treeFlattener
    );
    dataSource.data = filteredTree ? [filteredTree] : [];
    return dataSource;
  });

  cardArchitecture: CardConfig = {
    header: {
      title: {
        label: this.translateService.instant(
          'accountingArea.accounting_areas.architecture'
        ),
      },
    },
  };

  toggleInactive: boolean = false;

  ngOnInit() {
    this.treeDataSource().data = [];
    this.breadCrumbService.setBreadcrumbs([
      {
        label: this.translateService.instant('speedikon.breadCrumb'),
      },
    ]);

    this.locationFormlyFieldService.setSearchOption(this.toggleInactive);

    this.formFields = [
      ...this.locationFormlyFieldService.autocompleteSearch(
        false,
        null,
        true,
        id => this.onSelect(id),
        null,
        () => this.onDelete(),
        this.translateService.instant(
          'location.shared.delete_location_search_tooltip'
        )
      ),
    ];
  }

  ngOnDestroy() {
    this.speedikonService.archObjIdSignal$.next(null);
  }

  onSelect(archObjId: string): void {
    this.showDiv.set(false);
    this.speedikonService.load$.next(archObjId);

    const currentData = this.treeDataSignal().getArchobjById;
    if (currentData?.id === archObjId) {
      this.selectedLocation.set(currentData);
    }
  }

  onDelete(): () => void {
    this.selectedLocation = null;
    this.selectedItems.set([]);
    this.treeDataSource().data = [];
    this.showDiv.set(false);

    return null;
  }

  // eslint-disable-next-line class-methods-use-this
  hasChild = (_: number, node: FlatNode): boolean => node.expandable;

  onNodeClick(node: FlatNode) {
    this.speedikonService.archObjIdSignal$.next(node.id);
    this.showDiv.set(true);
  }

  onToggleChange(e) {
    this.locationFormlyFieldService.setSearchOption(e.checked);
  }
}
